import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DateTime } from 'luxon';
import { useSnackbar } from 'notistack';

import Layout from '../../layout/Layout';
import Card from '../../components/Card/Card';
import Table from '../../components/Table/Table';
import TableHead from '../../components/Table/TableHead';
import TableHeadData from '../../components/Table/TableHeadData';
import TableRow from '../../components/Table/TableRow';
import TableData from '../../components/Table/TableData';
import TableBody from '../../components/Table/TableBody';
import EmptyTable from '../../components/Table/EmptyTable';
import Button from '../../components/Button/Button';
import ButtonTypes from '../../constants/buttonTypes';
import Badge from '../../components/Badge/Badge';
import Modal from '../../components/Modal/Modal';
import Input from '../../components/Input/Input';
import Text from '../../components/Text/Text';
import CustomSelect from '../../components/Select/CustomSelect';
import Alert from '../../components/Alert/Alert';
import AlertType from '../../common/alertType';
import Pagination from '../../components/Pagination/Pagination';
import PermissionsGate from '../../components/PermissionsGate/PermissionsGate';
import AccessDenied from '../../components/AccessDenied/AccessDenied';
import { permissions } from '../../common/permissionGroup';
import {
  getServiceTokens, getServiceTokenUsers, createServiceToken, revokeServiceToken
} from '../../services/serviceTokens';

const errorMessage = (error) => Object.values(error?.errors || {}).flat()[0]
  || error?.data?.message || error?.message || 'The request failed. Please try again.';
const formatDate = (value) => (value ? DateTime.fromISO(value).toLocaleString(DateTime.DATETIME_SHORT) : 'Never');

const ServiceTokensContent = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [tokens, setTokens] = useState([]);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState();
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [name, setName] = useState('');
  const [formError, setFormError] = useState();
  const [busy, setBusy] = useState(false);
  const [secret, setSecret] = useState('');
  const [copied, setCopied] = useState(false);
  const [revoking, setRevoking] = useState(null);

  const loadTokens = useCallback(async (nextPage = 1) => {
    setLoading(true);
    setError(undefined);
    try {
      const result = await getServiceTokens(nextPage);
      setTokens(result.data);
      setPage(result.current_page);
      setLastPage(result.last_page);
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTokens(); }, [loadTokens]);

  const openCreate = async () => {
    setCreating(true);
    setSelectedUser(null);
    setName('');
    setFormError(undefined);
    setUsersLoading(true);
    setUsers([]);
    try {
      const result = await getServiceTokenUsers();
      setUsers(result.map((user) => ({
        value: user.id,
        label: `${user.email} — ${[user.name, user.last_name].filter(Boolean).join(' ')}`,
      })));
    } catch (requestError) {
      setFormError(errorMessage(requestError));
    } finally {
      setUsersLoading(false);
    }
  };

  const issueToken = async () => {
    if (busy || !selectedUser || !name.trim()) return;
    setBusy(true);
    setFormError(undefined);
    try {
      const result = await createServiceToken({ user_id: selectedUser.value, name: name.trim() });
      setSecret(result.token);
      setCopied(false);
      setCreating(false);
      loadTokens(1);
    } catch (requestError) {
      setFormError(errorMessage(requestError));
    } finally {
      setBusy(false);
    }
  };

  const copyToken = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
    } catch {
      enqueueSnackbar('Could not copy automatically. Select and copy the token manually.', { variant: 'warning' });
    }
  };

  const revokeToken = async () => {
    if (busy || !revoking) return;
    setBusy(true);
    setFormError(undefined);
    try {
      await revokeServiceToken(revoking.id);
      setRevoking(null);
      enqueueSnackbar('Service token revoked', { variant: 'success' });
      loadTokens(page);
    } catch (requestError) {
      setFormError(errorMessage(requestError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Card
        showHeader
        headerTitle="Service Tokens"
        headerRight={<Button text="Create token" onClick={openCreate} />}
        footer={(
          <Pagination currentPage={page} lastPage={lastPage} loading={loading} onPagePress={loadTokens} />
        )}
      >
        <Text
          muted
          text={'Service tokens remain valid until revoked. '
            + 'Blocking the user or changing their password invalidates their tokens.'}
          textStyle="mb-[20px]"
        />
        {error && <Alert text={error} type={AlertType.warning} />}
        <Table loading={loading} mobileVisibleFields={['Name', 'User', 'Status', 'Actions']}>
          <TableHead>
            <TableRow bordered>
              {['Name', 'User', 'Created', 'Last used', 'Status', 'Actions'].map((title) => (
                <TableHeadData key={title}>{title}</TableHeadData>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {tokens.map((token) => {
              const blocked = !token.tokenable || token.tokenable.account_status !== 'active';
              const inactive = !!token.revoked_at || blocked;
              let status = blocked ? 'User blocked' : 'Active';
              if (token.revoked_at) status = 'Revoked';
              return (
                <TableRow key={token.id} bordered>
                  <TableData><span className="break-all">{token.name}</span></TableData>
                  <TableData><span className="break-all">{token.tokenable?.email || 'User deleted'}</span></TableData>
                  <TableData>{formatDate(token.created_at)}</TableData>
                  <TableData>{formatDate(token.last_used_at)}</TableData>
                  <TableData><Badge text={status} type={inactive ? 'lightDanger' : 'lightSuccess'} /></TableData>
                  <TableData>
                    {!token.revoked_at && (
                      <Button
                        text="Revoke"
                        type={ButtonTypes.LIGHT_DANGER}
                        onClick={() => { setFormError(undefined); setRevoking(token); }}
                      />
                    )}
                  </TableData>
                </TableRow>
              );
            })}
            {!tokens.length && !loading && <EmptyTable colSpan={6} text="No service tokens" />}
          </TableBody>
        </Table>
      </Card>
      <Modal
        visible={creating}
        title="Create service token"
        toggleVisible={() => { if (!busy) setCreating(false); }}
        onSave={issueToken}
        saveText="Generate token"
        loading={busy}
        canSave={!selectedUser || !name.trim() || name.trim().length > 100 || usersLoading}
      >
        <div className="w-full sm:w-[460px] space-y-[20px]">
          {formError && <Alert text={formError} type={AlertType.warning} />}
          <div>
            <Text text="User" bold required textStyle="mb-[10px]" />
            <CustomSelect
              options={users}
              value={selectedUser}
              onChange={setSelectedUser}
              isLoading={usersLoading}
              isDisabled={busy}
              placeholder="Select a user"
            />
          </div>
          <div>
            <Text text="Token name" bold required textStyle="mb-[10px]" />
            <Input
              value={name}
              onChange={(event) => setName(event.target.value)}
              disabled={busy}
              placeholder="Warehouse integration"
            />
          </div>
          <Text
            muted
            text={"The token receives the user's role permissions at creation and remains valid until revoked. "
              + 'Role permission changes require a new token.'}
          />
        </div>
      </Modal>
      <Modal
        visible={!!secret}
        title="Token created"
        toggleVisible={() => { setSecret(''); setCopied(false); }}
        hideSaveBtn
        cancelButtonText="Done"
      >
        <div className="w-full sm:w-[460px] space-y-[20px]">
          <Alert
            type={AlertType.warning}
            text={'Copy and store this token securely. '
              + 'You will not be able to view it again after closing this window.'}
          />
          <textarea
            readOnly
            value={secret}
            aria-label="Service token"
            rows={3}
            className="w-full p-[16px] rounded-[6px] bg-input-bg text-text-main
              dark:bg-[#1b263b] dark:text-secondary font-mono break-all"
            onFocus={(event) => event.target.select()}
          />
          <Button text={copied ? 'Copied' : 'Copy token'} onClick={copyToken} />
        </div>
      </Modal>
      <Modal
        visible={!!revoking}
        title="Revoke service token?"
        toggleVisible={() => { if (!busy) setRevoking(null); }}
        hideSaveBtn
        cancelButtonText="Cancel"
        additionalButton={(
          <Button text="Revoke token" type={ButtonTypes.DANGER} disabled={busy} onClick={revokeToken} />
        )}
      >
        <div className="w-full sm:w-[460px] space-y-[20px]">
          {formError && <Alert text={formError} type={AlertType.warning} />}
          <Text
            text={`Revoke “${revoking?.name || ''}”? `
              + 'Integrations using this token will lose access immediately. This cannot be undone.'}
          />
        </div>
      </Modal>
    </>
  );
};

const ServiceTokens = () => {
  const navigate = useNavigate();
  return (
    <Layout breadcrumbs={{
      pageTitle: 'Service Tokens',
      history: [
        { title: 'Home', onClick: () => navigate('/') },
        { title: 'User management' },
        { title: 'Service Tokens' },
      ],
    }}
    >
      <PermissionsGate can={permissions.ADMIN_ADMIN} errorComponent={<AccessDenied />}>
        <ServiceTokensContent />
      </PermissionsGate>
    </Layout>
  );
};

export default ServiceTokens;
